import React, { useEffect } from 'react';
import styled from 'styled-components/native';
import {
  ImageBackground,
  TouchableOpacity,
  useWindowDimensions,
  Share,
  Platform
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { addDays, differenceInDays, isBefore } from 'date-fns';
import { MaterialIcons } from '@expo/vector-icons';
import { StudyParticipant } from '../../../models/StudyParticipant';
import Colors from '../../../constants/Colors';
import { calculateStudyParticipantDates } from '../../../common/calculateStudyParticipantDates';
import { useGetStudyIntake } from '../../../hooks/study/useGetStudyIntake';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { StudiesParamList } from '../../../navigation/StudiesNavigator';
import { hasCompletedFormTemplateFields } from '../../../common/hasCompletedFormTemplateFields';
import useAddStudyToFavorite from '../../../hooks/user-favorite/useAddStudyParticipantToFavorite';
import useDeleteStudyParticipantToFavorite from '../../../hooks/user-favorite/useDeleteStudyParticipantToFavorite';
import { log } from '../../../tools/LogHelper';
import { stringify } from 'flatted';
import elementIDs from '../../../../elementIDs';
import { useUserContext } from '../../../context/UserContext';

const StyledContainer = styled.TouchableOpacity<{ singleColumn: boolean }>`
  border-radius: 24px;
  shadow-color: #111111;
  shadow-offset: 0 16px;
  shadow-opacity: 0.1;
  shadow-radius: 48px;
  width: ${p => (p.singleColumn ? '100%' : '330px')};
  flex-direction: row;
  height: 185px;
`;

const StyledCardView = styled(ImageBackground)`
  flex: 1;
  border-radius: 12px;
  padding: 21px;
`;

const StyledTitleView = styled.View`
  width: 100%;
  align-items: flex-start;
`;

const StyledTitleText = styled.Text<{ textColor?: string }>`
  font-family: 'DMSans-Bold';
  font-size: 18px;
  line-height: 23px;
  color: ${p => (p.textColor ? p.textColor : '#f0f3ff')};
  max-width: 180px;
`;

const StyledStudyLengthText = styled.Text<{ textColor?: string }>`
  font-family: 'DMSans-Regular';
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: ${p => (p.textColor ? p.textColor : '#f0f3ff')};
  margin-top: 8px;
`;

const StyledIconView = styled.View`
  width: 100%;
  position: absolute;
  align-self: flex-start;
  flex-direction: row;
  justify-content: flex-end;
`;

const StyledFeather = styled(Feather)`
  margin-left: 12px;
`;

const StyledAccessoryView = styled.View`
  flex: 1;
  justify-content: flex-end;
  align-items: flex-end;
`;

const StyledProgressText = styled.Text<{ textColor?: string }>`
  align-self: flex-end;
  font-family: 'DMSans-Bold';
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  margin-right: 8px;
  color: ${p => (p.textColor ? p.textColor : '#f0f3ff')};
`;

const StyledBottomView = styled.View`
  margin-top: 12px;
  flex-direction: row;
  flex: 1;
`;

const getCardColor = (status: string) => {
  switch (status) {
    case 'planning':
      return Colors.chloePink;
    case 'progress':
      return Colors.progressBackgroundColor;
    case 'paused':
      return Colors.pausedBackgroundColor;
    case 'recruiting':
      return Colors.recruitingBackgroundColor;
    case 'completed':
      return Colors.primaryPurple;
    default:
      return 'transparent';
  }
};

export interface StudyParticipantCardProps {
  studyParticipant: StudyParticipant;
  imageUrl?: string;
  backgroundColor?: string;
  accessory?: JSX.Element;
  favorite?: boolean;
  onFavorite?: () => void;
  onShare?: () => void;
  textColor?: string;
}

const StudyParticipantCard: React.FC<StudyParticipantCardProps> = ({
  studyParticipant,
  imageUrl,
  backgroundColor,
  accessory,
  textColor,
  favorite,
  onFavorite,
  onShare
}) => {
  const window = useWindowDimensions();
  const singleColumn = window.width < 768;
  const navigation = useNavigation<StackNavigationProp<StudiesParamList>>();
  const modalNavigation = useNavigation<StackNavigationProp<any>>();
  const { user } = useUserContext();
  const { id: studyParticipantId, userId, studyId, study } = studyParticipant;
  const {
    startDate,
    studyLength: length,
    projectedStudyLength,
    isCompletedStudy
  } = calculateStudyParticipantDates(study, studyParticipant);

  const studyLength = projectedStudyLength || length;
  const { getStudyIntake } = useGetStudyIntake();
  const {
    addStudyToFavoriteData,
    addStudyToFavorite,
    clearAddStudyToFavoriteData
  } = useAddStudyToFavorite(userId, studyParticipantId);

  const {
    deleteStudyToFavoriteData: deleteToFavoriteData,
    deleteStudyToFavorite,
    clearDeleteStudyToFavoriteData: clearDeleteToFavoriteData
  } = useDeleteStudyParticipantToFavorite(userId, studyParticipantId);

  useEffect(() => {
    if (addStudyToFavoriteData) {
      onFavorite?.();
      clearAddStudyToFavoriteData();
    }
  }, [addStudyToFavoriteData, clearAddStudyToFavoriteData, onFavorite]);

  useEffect(() => {
    if (deleteToFavoriteData) {
      clearDeleteToFavoriteData();
      onFavorite?.();
    }
  }, [clearDeleteToFavoriteData, deleteToFavoriteData, onFavorite]);

  const status = studyParticipant?.paused
    ? isCompletedStudy
      ? 'completed'
      : 'paused'
    : startDate
    ? isCompletedStudy
      ? 'completed'
      : isBefore(startDate, new Date())
      ? 'progress'
      : 'planning'
    : 'planning';
  const isPendingStudy = Boolean(
    studyParticipant?.study?.qualificationFormTemplateId &&
      !studyParticipant?.study?.isPublic &&
      !studyParticipant?.inviteSent
  );
  return (
    <StyledContainer
      singleColumn={singleColumn}
      activeOpacity={0.8}
      testID={elementIDs.studyCardID}
      disabled={isPendingStudy}
      onPress={async () => {
        if (
          status === 'planning' ||
          // double check study intake for sponsored studies
          /* study?.type === StudyType.Sponsor && */ studyParticipant?.startDate
        ) {
          // load the intake form to check if its completed
          const studyIntake = await getStudyIntake(studyParticipantId);
          if (
            studyIntake?.formTemplate &&
            !hasCompletedFormTemplateFields(
              studyIntake.formTemplate,
              studyIntake,
              studyIntake.studyParticipant?.user ?? user
            )
          ) {
            modalNavigation.navigate('ModalNavigator', {
              screen: 'FormTemplate',
              params: {
                formTemplateId: studyIntake?.formTemplateId,
                entityDataId: studyIntake?.id,
                studyParticipantId
              }
            });
          } else if (studyIntake) {
            // completed form so redirect to study screen
            navigation.push('StudyInfo', {
              studyId: studyId,
              studyParticipantId: studyParticipantId
            });
          }
        } else {
          // consumer studies navigate to the study screen
          // navigation.push to prevent wrong params on web
          navigation.push('StudyInfo', {
            studyId,
            studyParticipantId
          });
        }
      }}
    >
      <StyledCardView
        imageStyle={{
          backgroundColor: imageUrl
            ? 'white'
            : backgroundColor || getCardColor(status),
          borderRadius: 12
        }}
        source={{ uri: imageUrl }}
      >
        <StyledTitleView>
          <StyledTitleText textColor={textColor}>{study?.name}</StyledTitleText>
          {study?.isPediatric &&
            studyParticipant.user &&
            studyParticipant.userId !== user?.id && (
              <StyledStudyLengthText textColor={textColor}>
                {[
                  studyParticipant.user.firstName,
                  studyParticipant.user.lastName
                ]
                  .filter(Boolean)
                  .join(' ')}
              </StyledStudyLengthText>
            )}
          <StyledIconView>
            <TouchableOpacity
              testID={elementIDs.studyFavoriteIconID}
              onPress={() => {
                if (favorite) {
                  deleteStudyToFavorite();
                } else {
                  addStudyToFavorite();
                }
              }}
            >
              <MaterialIcons
                name={favorite ? 'favorite' : 'favorite-outline'}
                color={textColor ? textColor : 'white'}
                size={24}
              />
            </TouchableOpacity>
            {Platform.OS != 'web' && study?.shareUrl && (
              <TouchableOpacity
                onPress={async () => {
                  await Share.share({
                    message: study?.shareUrl || ''
                  })
                    .catch(error => {
                      log('onShare error', stringify(error));
                    })
                    .then(() => {
                      onShare?.();
                    });
                }}
              >
                <StyledFeather
                  name='share'
                  size={24}
                  color={textColor ? textColor : 'white'}
                />
              </TouchableOpacity>
            )}
          </StyledIconView>
          {startDate ? (
            <StyledStudyLengthText textColor={textColor}>{`(${
              startDate.getMonth() + 1
            }/${startDate.getDate()} - ${
              addDays(startDate, studyLength).getMonth() + 1
            }/${addDays(
              startDate,
              studyLength !== 0 ? studyLength - 1 : studyLength
            ).getDate()})`}</StyledStudyLengthText>
          ) : (
            <StyledStudyLengthText>
              {`Duration: ${studyLength} ${studyLength === 1 ? 'day' : 'days'}`}
            </StyledStudyLengthText>
          )}
        </StyledTitleView>
        <StyledBottomView>
          {startDate && (
            <StyledProgressText textColor={textColor}>
              {status === 'paused'
                ? 'Paused'
                : isCompletedStudy
                ? '100% complete'
                : `${Number(
                    (studyLength !== 0
                      ? (differenceInDays(new Date(), startDate) /
                          studyLength) *
                          100 <
                        100
                        ? (differenceInDays(new Date(), startDate) /
                            studyLength) *
                          100
                        : 100
                      : 100
                    ).toFixed(1)
                  )}% complete`}
            </StyledProgressText>
          )}
          {!!accessory && (
            <StyledAccessoryView>{accessory}</StyledAccessoryView>
          )}
        </StyledBottomView>
      </StyledCardView>
    </StyledContainer>
  );
};

export default StudyParticipantCard;
